import React, { useEffect, useCallback } from 'react'
import { Divider, Typography, Spin, message } from 'antd'
import { DndProvider } from 'react-dnd'
import CarouselItem from './carousel-item'
import Carousel from './carousel'
import { HTML5Backend } from 'react-dnd-html5-backend'
import useForceRender from 'src/utils/ForceRender'
import { clone } from 'ramda'
import * as api from 'src/api/track'
import AddingModal from './addTracks-modal'
import s from './index.module.scss'

const { Title } = Typography

const TrackDiscovery: React.FC = () => {
  const forceRender = useForceRender()
  const selectedCategory = React.useRef<TracksCategory>(null)
  const showAdding = React.useRef<boolean>(false)
  const loading = React.useRef<boolean>(true)
  const data = React.useRef<any[]>([])

  const loadPageData = useCallback(() => {
    api.getTrackCategories().then((categories) => {
      loading.current = false
      data.current = categories
      forceRender()
    })
  }, [])

  useEffect(() => {
    loadPageData()
  }, [])

  const onModalCancel = React.useCallback(() => {
    selectedCategory.current = null
    showAdding.current = false
    forceRender()
  }, [])

  const onAddClick = React.useCallback((category: TracksCategory) => {
    selectedCategory.current = category
    showAdding.current = true
    forceRender()
  }, [])

  const onModalDone = React.useCallback(() => {
    loading.current = true
    onModalCancel()
    loadPageData()
  }, [])

  const updateOrdering = React.useCallback(
    async (dragId: string, dropId: string, category: TracksCategory) => {
      const categoryNew = data.current.find(
        (cat: TracksCategory) => cat.id === category.id
      ) as TracksCategory

      const dragIndex = categoryNew.tracks.findIndex((i) => i.id === dragId)

      const dropIdIndex = categoryNew.tracks.findIndex((i) => i.id === dropId)

      let dropIndex: number

      if (dropIdIndex === 0) {
        dropIndex = dropIdIndex
      } else if (dragIndex > dropIdIndex) {
        dropIndex = dropIdIndex
      } else {
        dropIndex = dropIdIndex + 1
      }

      const dragItem = categoryNew.tracks[dragIndex]
      const tempItem = { id: 'temp' } as any
      const cloneItems = clone(categoryNew.tracks)
      cloneItems[dragIndex] = tempItem
      cloneItems.splice(dropIndex, 0, dragItem)

      const orderedItems = cloneItems.filter((i) => i.id !== 'temp')

      const trackIds = orderedItems.map((i) => i.id)
      loading.current = true
      forceRender()
      await api.updateTracksInsideCategory(
        categoryNew.id,
        categoryNew.type,
        trackIds
      )
      message.success(`Updated Tracks ordering of ${categoryNew.name}`)
      loadPageData()
    },
    [data]
  )

  const handleRemoving = React.useCallback(
    async (track: Tracks, category: TracksCategory) => {
      loading.current = true
      forceRender()
      const trackIds = category.tracks?.reduce((IDs, current) => {
        if (current.id !== track.id) {
          IDs.push(current.id)
        }
        return IDs
      }, [])
      await api.updateTracksInsideCategory(category.id, category.type, trackIds)
      message.success(`${track.title} is removed from ${category.type}`)
      loadPageData()
    },
    []
  )

  return (
    <Spin spinning={loading.current}>
      <Title level={3}>Tracks</Title>
      <Divider />
      <DndProvider backend={HTML5Backend}>
        {data.current?.map((category) => (
          <Carousel
            key={category.id}
            name={category.type}
            onAddClick={() => onAddClick(category)}
          >
            {category.tracks?.length ? (
              category.tracks.map((item) => (
                <CarouselItem
                  key={item.id}
                  track={item}
                  categoryId={category.id}
                  onDrop={(drapId, dropId) =>
                    updateOrdering(drapId, dropId, category)
                  }
                  onRemove={(track) => handleRemoving(track, category)}
                />
              ))
            ) : (
              <span className={s.noData}> No data</span>
            )}
          </Carousel>
        ))}
      </DndProvider>
      <AddingModal
        visible={showAdding.current}
        category={selectedCategory.current}
        onDone={onModalDone}
        onCancel={onModalCancel}
      />
    </Spin>
  )
}

TrackDiscovery.displayName = 'Discovery'

export default TrackDiscovery
